/**
 * Modal — กล่องโต้ตอบที่ใช้ซ้ำได้ของ Desktop shell (`WO-P2-UI-002`)
 *
 * เกิดขึ้นเพราะหน้าจอ "แผนก" ย้ายฟอร์มสร้างแผนกจากการ์ดที่แปะค้างอยู่บนหน้า เข้ามา
 * อยู่หลังปุ่ม "สร้างแผนก" — และหน้าจอสร้างอื่น ๆ ของ Phase 2 จะต้องการสิ่งเดียวกัน
 * ตัวนี้จึงอยู่ที่ `desktop/components/` ไม่ใช่ในโฟลเดอร์ของหน้าจอใดหน้าจอหนึ่ง
 *
 * **กล่องนี้รับผิดชอบเฉพาะ "การเป็นกล่อง"** — เปิด/ปิด, การ์ดโฟกัส และ ARIA ส่วน
 * ฟอร์ม, การตรวจความถูกต้อง, คำขอ และการแจ้งผล เป็นของหน้าจอที่เรียกใช้ทั้งหมด
 * (`children`) กล่องนี้ไม่รู้จัก client, สิทธิ์ หรือ contract ใดเลย
 *
 * สิ่งที่ต้องถูกต้องเพราะเป็นเรื่องการเข้าถึง ไม่ใช่เรื่องความสวยงาม:
 *
 * - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` ชี้ไปที่หัวเรื่อง เพื่อให้
 *   screen reader ประกาศว่ากำลังอยู่ใน dialog ชื่ออะไร
 * - **โฟกัสเข้ามาข้างในตอนเปิด และกลับไปที่ปุ่มที่กดตอนปิด** — คนที่ใช้คีย์บอร์ดอย่าง
 *   เดียวจะหลงทางทันทีถ้าโฟกัสยังค้างอยู่หลังฉาก หรือถูกทิ้งไว้ที่ `<body>` หลังปิด
 * - **โฟกัสวนอยู่ในกล่อง** ตราบที่กล่องเปิดอยู่ — พื้นหลังถูกปิดกั้นทางสายตาแล้ว จึง
 *   ต้องถูกปิดกั้นทางคีย์บอร์ดด้วย ไม่งั้น Tab จะพาไปกดปุ่มที่มองไม่เห็น
 * - ปิดได้สามทางที่ผู้ใช้คาดหวัง: Esc, ปุ่มปิดที่ **มองเห็นได้**, และคลิกฉากหลัง
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import './modal.css';

export interface ModalProps {
  /** `false` = ไม่เรนเดอร์อะไรเลย (ไม่ใช่แค่ซ่อน) — ฟอร์มข้างในจึงเริ่มใหม่ทุกครั้งที่เปิด */
  readonly open: boolean;
  /** ถูกเรียกเมื่อผู้ใช้ขอปิด (Esc / ปุ่มปิด / ฉากหลัง) — เจ้าของ state เป็นผู้ปิดจริง */
  readonly onClose: () => void;
  /** หัวเรื่องที่ทั้งตาและ screen reader ใช้ — ผูกเป็น `aria-labelledby` ของ dialog */
  readonly title: string;
  readonly children: ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(root: HTMLElement): readonly HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export function Modal({ open, onClose, title, children }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  /** element ที่ถือโฟกัสอยู่ก่อนกล่องเปิด — ปกติคือปุ่มที่ผู้ใช้เพิ่งกด */
  const returnFocusRef = useRef<HTMLElement | null>(null);

  /**
   * โฟกัสเข้าและกลับ — ผูกกับ `open` เท่านั้น
   *
   * ถ้าผูกกับอย่างอื่นด้วย การ re-render ระหว่างที่กล่องเปิดอยู่ (เช่นผู้ใช้พิมพ์ชื่อแผนก)
   * จะดึงโฟกัสกลับไปที่ช่องแรกกลางคัน ซึ่งเป็นพฤติกรรมที่ทำให้พิมพ์ต่อไม่ได้
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const dialog = dialogRef.current;
    if (dialog !== null) {
      // ช่องแรกของเนื้อหาก่อน แล้วค่อยถอยมาที่ตัว dialog เอง — คนที่เปิดกล่องสร้าง
      // ของมาเพื่อพิมพ์ ไม่ได้มาเพื่อกดปุ่มปิด
      const body = dialog.querySelector<HTMLElement>('[data-modal-body]');
      const target =
        (body === null ? undefined : focusableWithin(body)[0]) ??
        focusableWithin(dialog)[0] ??
        dialog;
      target.focus();
    }

    return () => {
      // ปุ่มที่กดอาจหายไปแล้ว (เช่นสิทธิ์เปลี่ยน) — `focus()` บน element ที่หลุด DOM
      // ไม่ทำอะไรเลย ไม่ต้องกันเพิ่ม
      returnFocusRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // กันไม่ให้ Esc ทะลุไปสั่งอย่างอื่นบนหน้าจอที่อยู่หลังกล่อง
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  /** Tab ที่ขอบกล่องวนกลับอีกฝั่ง แทนที่จะหลุดไปหาหน้าจอที่อยู่หลังฉาก */
  const trapTab = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }

    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }

    const focusable = focusableWithin(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) {
    return null;
  }

  // portal ไปที่ `<body>` — กล่องต้องอยู่เหนือทุกอย่างโดยไม่ต้องไปแก้ z-index/overflow
  // ของการ์ดหรือตารางที่บังเอิญครอบมันอยู่
  return createPortal(
    <div className="cap-modal" data-testid="cap-modal">
      {/**
       * ฉากหลังไม่รับโฟกัสและไม่อยู่ในลำดับ Tab โดยตั้งใจ — ทางปิดด้วยคีย์บอร์ดคือ
       * Esc และปุ่มปิดที่มองเห็นได้ การใส่ปุ่มซ่อนไว้ตรงนี้มีแต่จะเพิ่มจุดแวะที่
       * screen reader อ่านไม่ได้ความ
       */}
      <div
        className="cap-modal__backdrop"
        data-testid="cap-modal-backdrop"
        onClick={onClose}
      />
      <div
        className="cap-modal__dialog"
        data-testid="cap-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={trapTab}
      >
        <div className="cap-modal__head">
          <h2 className="cap-modal__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="cap-modal-close"
            onClick={onClose}
          >
            ปิด
          </button>
        </div>
        <div className="cap-modal__body" data-modal-body>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
